<template>
  <div>
    <div class="page-head">
      <div>
        <h2>高端定制管理</h2>
        <p>按小程序实际版式编辑 Hero、四项权益和四步定制流程；图标与编号由页面统一维护。</p>
      </div>
    </div>

    <div v-loading="loading" class="custom-service-editor">
      <el-form ref="form" :model="form" label-position="top">
        <el-card class="section-card" shadow="never">
          <div slot="header" class="section-head">
            <div><strong>Hero 首屏</strong><span>图片和文案修改后会直接同步到小程序顶部大图。</span></div>
            <el-tag size="small" effect="plain">按钮与徽标图标固定</el-tag>
          </div>
          <el-row :gutter="20">
            <el-col :xs="24" :md="12">
              <el-form-item label="第一行主标题">
                <el-input v-model="form.title" maxlength="12" show-word-limit placeholder="例如：北京私享定制" />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12">
              <el-form-item label="第二行主标题">
                <el-input v-model="form.subtitle" maxlength="18" show-word-limit placeholder="例如：1 对 1 方案" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="Hero 头图">
            <asset-upload v-model="form.hero_url" category="custom-service" />
          </el-form-item>
          <el-form-item label="核心说明">
            <el-input v-model="form.description" type="textarea" :rows="2" maxlength="36" show-word-limit placeholder="例如：不收前期定金，定制师免费设计行程方案" />
          </el-form-item>
          <el-form-item label="金色服务亮点">
            <el-input v-model="form.badge" maxlength="80" show-word-limit placeholder="例如：故宫 · 长城 · 胡同 · 酒店 · 专车 · 讲解" />
            <div class="field-help">显示在核心说明下方；建议用“·”分隔，保持一行。</div>
          </el-form-item>
        </el-card>

        <el-card class="section-card" shadow="never">
          <div slot="header" class="section-head">
            <div><strong>四项核心权益</strong><span>图标和位置固定，只需要填写用户看到的标题与说明。</span></div>
            <el-tag size="small" type="warning" effect="plain">固定 4 项</el-tag>
          </div>
          <div class="fixed-grid">
            <div v-for="(item, index) in valueItems" :key="`value-${index}`" class="item-editor">
              <div class="item-editor-head">
                <span class="item-number">{{ String(index + 1).padStart(2, '0') }}</span>
                <strong>{{ valueIconLabels[index] }}</strong>
                <el-tag size="mini" type="info" effect="plain">固定图标</el-tag>
              </div>
              <el-form-item label="标题">
                <el-input v-model="item.title" maxlength="12" show-word-limit />
              </el-form-item>
              <el-form-item label="说明">
                <el-input v-model="item.description" maxlength="24" show-word-limit />
              </el-form-item>
            </div>
          </div>
        </el-card>

        <el-card class="section-card" shadow="never">
          <div slot="header" class="section-head">
            <div><strong>四步定制流程</strong><span>序号自动生成，不再需要手写“01｜标题｜说明”。</span></div>
            <el-tag size="small" effect="plain">固定 4 步</el-tag>
          </div>
          <div class="fixed-grid">
            <div v-for="(item, index) in processItems" :key="`process-${index}`" class="item-editor">
              <div class="item-editor-head">
                <span class="process-number">{{ String(index + 1).padStart(2, '0') }}</span>
                <strong>第 {{ index + 1 }} 步</strong>
              </div>
              <el-form-item label="步骤标题">
                <el-input v-model="item.title" maxlength="12" show-word-limit />
              </el-form-item>
              <el-form-item label="步骤说明">
                <el-input v-model="item.description" type="textarea" :rows="2" maxlength="36" show-word-limit />
              </el-form-item>
            </div>
          </div>
        </el-card>

        <el-card class="section-card" shadow="never">
          <div slot="header" class="section-head">
            <div><strong>服务包含</strong><span>用于高端定制详情页，可单独增删和调整顺序。</span></div>
            <el-button type="primary" plain size="small" icon="el-icon-plus" @click="addInclude">新增服务项</el-button>
          </div>
          <div v-if="includeItems.length" class="include-list">
            <div v-for="(item, index) in includeItems" :key="item.editorKey" class="include-editor">
              <div class="include-order">
                <strong>服务项 {{ index + 1 }}</strong>
                <div>
                  <el-button type="text" :disabled="index === 0" @click="moveInclude(index, -1)">上移</el-button>
                  <el-button type="text" :disabled="index === includeItems.length - 1" @click="moveInclude(index, 1)">下移</el-button>
                  <el-button type="text" class="danger-action" @click="removeInclude(index)">删除</el-button>
                </div>
              </div>
              <el-row :gutter="16">
                <el-col :xs="24" :md="8"><el-form-item label="标题"><el-input v-model="item.title" maxlength="20" /></el-form-item></el-col>
                <el-col :xs="24" :md="16"><el-form-item label="说明"><el-input v-model="item.description" maxlength="80" /></el-form-item></el-col>
              </el-row>
            </div>
          </div>
          <el-empty v-else :image-size="72" description="暂无服务项，点击右上角新增" />
        </el-card>

        <el-card class="section-card" shadow="never">
          <div slot="header" class="section-head"><div><strong>其他设置</strong><span>控制详情页补充说明和小程序展示状态。</span></div></div>
          <el-form-item label="方案确认说明">
            <el-input v-model="form.notice" type="textarea" :rows="2" maxlength="80" show-word-limit />
            <div class="field-help">用于服务详情页的流程说明，不占用本页首屏空间。</div>
          </el-form-item>
          <el-form-item label="展示状态">
            <el-select v-model="form.status">
              <el-option label="展示中" value="active" />
              <el-option label="已隐藏" value="inactive" />
            </el-select>
          </el-form-item>
        </el-card>
      </el-form>

      <div class="save-bar">
        <span>保存后，小程序重新进入页面即可看到最新内容。</span>
        <el-button type="primary" :loading="saving" @click="save">保存高端定制</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import AssetUpload from "../components/AssetUpload.vue";
import { createResource, listResource, updateResource } from "../api/resources";

const VALUE_DEFAULTS = [
  { title: "免定金", description: "确认方案后再支付" },
  { title: "1 对 1 定制师", description: "先沟通需求再设计" },
  { title: "专车接送", description: "按行程灵活安排" },
  { title: "酒店精选", description: "按等级和偏好筛选" }
];

const PROCESS_DEFAULTS = [
  { title: "提交需求", description: "告知您的想法与偏好" },
  { title: "定制师沟通", description: "一对一深度沟通了解需求" },
  { title: "免费出方案", description: "量身设计行程提供专业建议" },
  { title: "确认后支付", description: "确认行程与服务安心出行" }
];

let editorKey = 0;

export default {
  name: "CustomServiceManage",
  components: { AssetUpload },
  data() {
    return {
      loading: false,
      saving: false,
      form: this.emptyForm(),
      valueItems: VALUE_DEFAULTS.map((item) => ({ ...item })),
      processItems: PROCESS_DEFAULTS.map((item) => ({ ...item })),
      includeItems: [],
      valueIconLabels: ["免定金盾牌", "1 对 1 定制师", "专车接送", "酒店精选"]
    };
  },
  mounted() {
    this.fetchConfig();
  },
  methods: {
    emptyForm() {
      return {
        title: "北京私享定制",
        subtitle: "1 对 1 方案",
        badge: "故宫 · 长城 · 胡同 · 酒店 · 专车 · 讲解",
        hero_url: "",
        description: "不收前期定金，定制师免费设计行程方案",
        notice: "确认方案后，按最终一价全含服务方案支付。",
        status: "active",
        sort_order: 0
      };
    },
    async fetchConfig() {
      this.loading = true;
      try {
        const res = await listResource("custom-service-config", { page: 1, pageSize: 1 });
        const row = res.data && res.data.items && res.data.items[0];
        this.form = { ...this.emptyForm(), ...(row || {}) };
        this.valueItems = this.fixedItems(this.parseJson(this.form.values_json || this.form.values), VALUE_DEFAULTS);
        this.processItems = this.fixedItems(this.parseJson(this.form.process_json || this.form.process || this.form.flow), PROCESS_DEFAULTS);
        this.includeItems = this.parseJson(this.form.include_json || this.form.includes).map((item) => this.editableItem(item));
      } finally {
        this.loading = false;
      }
    },
    parseJson(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },
    normalizeItem(item) {
      if (typeof item === "string") return { title: item, description: "" };
      return {
        title: (item && (item.title || item.name)) || "",
        description: (item && (item.description || item.desc)) || ""
      };
    },
    fixedItems(items, defaults) {
      return defaults.map((fallback, index) => ({
        ...fallback,
        ...(items[index] ? this.normalizeItem(items[index]) : {})
      }));
    },
    editableItem(item = {}) {
      return { ...this.normalizeItem(item), editorKey: `include-${++editorKey}` };
    },
    addInclude() {
      this.includeItems.push(this.editableItem());
    },
    removeInclude(index) {
      this.includeItems.splice(index, 1);
    },
    moveInclude(index, offset) {
      const target = index + offset;
      if (target < 0 || target >= this.includeItems.length) return;
      const list = this.includeItems.slice();
      const [item] = list.splice(index, 1);
      list.splice(target, 0, item);
      this.includeItems = list;
    },
    cleanItems(items, withIndex = false) {
      return items.map((item, index) => ({
        ...(withIndex ? { index: String(index + 1).padStart(2, "0") } : {}),
        title: String(item.title || "").trim(),
        description: String(item.description || "").trim(),
        icon_key: ""
      }));
    },
    validateContent() {
      const required = [...this.valueItems, ...this.processItems];
      if (required.some((item) => !String(item.title || "").trim())) {
        this.$message.error("四项核心权益和四步流程的标题都不能为空");
        return false;
      }
      if (this.includeItems.some((item) => !String(item.title || "").trim())) {
        this.$message.error("服务包含项目的标题不能为空");
        return false;
      }
      return true;
    },
    buildPayload() {
      return {
        ...this.form,
        badge: String(this.form.badge || "").trim(),
        values_json: this.cleanItems(this.valueItems),
        process_json: this.cleanItems(this.processItems, true),
        include_json: this.cleanItems(this.includeItems)
      };
    },
    async save() {
      if (!this.validateContent()) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (payload.id) await updateResource("custom-service-config", payload.id, payload);
        else await createResource("custom-service-config", payload);
        this.$message.success("高端定制配置已保存");
        await this.fetchConfig();
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.custom-service-editor { padding-bottom: 84px; }
.section-card { margin-bottom: 18px; border-radius: 10px; }
.section-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.section-head > div:first-child { display: flex; align-items: baseline; gap: 12px; min-width: 0; }
.section-head strong { color: #20242b; font-size: 16px; }
.section-head span { color: #8a9099; font-size: 13px; }
.field-help { margin-top: 6px; color: #909399; font-size: 12px; line-height: 1.5; }
.fixed-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.item-editor { padding: 16px 16px 2px; background: #fafbfc; border: 1px solid #e8ebf0; border-radius: 9px; }
.item-editor-head { display: flex; align-items: center; gap: 9px; margin-bottom: 14px; }
.item-editor-head strong { flex: 1; color: #30343b; font-size: 14px; }
.item-number,
.process-number { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; color: #fff; background: #a98446; border-radius: 50%; font-size: 12px; }
.process-number { background: #193655; }
.include-list { display: grid; gap: 12px; }
.include-editor { padding: 14px 16px 0; background: #fafbfc; border: 1px solid #e8ebf0; border-radius: 9px; }
.include-order { display: flex; align-items: center; justify-content: space-between; margin-bottom: 2px; }
.include-order strong { color: #30343b; font-size: 14px; }
.danger-action { color: #f56c6c; }
.save-bar { position: fixed; right: 26px; bottom: 0; left: var(--admin-aside-width, 220px); z-index: 20; display: flex; align-items: center; justify-content: flex-end; gap: 18px; min-height: 64px; padding: 0 28px; color: #7f858e; background: rgba(255, 255, 255, 0.96); border-top: 1px solid #e7eaf0; box-shadow: 0 -6px 18px rgba(31, 37, 48, 0.06); font-size: 13px; }
@media (max-width: 1100px) {
  .fixed-grid { grid-template-columns: 1fr; }
  .section-head,
  .section-head > div:first-child { align-items: flex-start; flex-direction: column; }
  .save-bar { left: 0; }
}
</style>
